// InvoicePreviewModal.jsx

import React, { useCallback, useMemo, useRef } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { openPrintDocument } from 'utilities/Print';
import { extractRichTextPlainText } from 'utilities/RichText';
import { DOCUMENT_PREVIEW_PRINT_STYLES } from 'nodetypes/documents/kit/documentPreviewPrintStyles';
import {
  INVOICE_TYPE_KEY,
  readInvoiceField,
} from 'nodetypes/invoice/view/invoiceGeneralInfoModel';

// CSS
import './InvoicePreviewModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import DocumentPayments from 'nodetypes/documents/payments/DocumentPayments';
import DocumentRichTextSection from 'nodetypes/documents/kit/DocumentRichTextSection';
import InvoiceGeneralInfo from 'nodetypes/invoice/view/InvoiceGeneralInfo';
import InvoicePreviewItems from 'nodetypes/invoice/view/InvoicePreviewItems';

function cleanString(value) {
  return String(value || '').trim();
}

function hasRichTextContent(node, fieldName, nodeType) {
  const typeKey = cleanString(nodeType?.key || node?.type || INVOICE_TYPE_KEY).toUpperCase();
  const value = readInvoiceField(node, fieldName, nodeType);
  if (value !== undefined) {
    return Boolean(extractRichTextPlainText(value));
  }
  const fallback = readNodeFieldRawValue(
    node,
    { name: fieldName, source: typeKey.toLowerCase() },
    typeKey,
  );
  return Boolean(extractRichTextPlainText(fallback));
}

/**
 * InvoicePreviewModal
 *
 * Designer-only preview of the client-facing invoice document.
 *
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {object|null} node
 * @param {object|null} [nodeType]
 * @param {object[]} [neighborNodes]
 * @param {object|null} [letterheadOverride]
 * @returns {ReactNode}
 */
const InvoicePreviewModal = React.memo(({
  isOpen = false,
  onClose,
  node = null,
  nodeType = null,
  neighborNodes = [],
  letterheadOverride = null,
}) => {
  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- REFS ----- //

  const sheetRef = useRef(null);

  // ----- MEMOS ----- //

  const printTitle = useMemo(() => {
    const title = cleanString(node?.title);
    const number = cleanString(readInvoiceField(node, 'number', nodeType));
    if (title && number) return `${title} (${number})`;
    return title || number || 'Invoice Preview';
  }, [node, nodeType]);

  const showTerms = useMemo(
    () => hasRichTextContent(node, 'termsAndConditions', nodeType),
    [node, nodeType],
  );
  const showMemo = useMemo(
    () => hasRichTextContent(node, 'memo', nodeType),
    [node, nodeType],
  );

  // ----- HANDLERS ----- //

  /**
   * Opens the browser print dialog for the preview sheet.
   *
   * @returns {void}
   */
  const handlePrint = useCallback(() => {
    const markup = sheetRef.current?.outerHTML;
    if (!markup) return;
    openPrintDocument({
      bodyMarkup: markup,
      styles: DOCUMENT_PREVIEW_PRINT_STYLES,
      title: printTitle,
    });
  }, [printTitle]);

  if (!isOpen || !node) return null;

  // ----- RENDER ----- //

  return (
    <Modal
      title="Invoice Preview"
      iconKey="visibility"
      isOpen={isOpen}
      onClose={onClose}
      width={isMobile ? '100%' : '960px'}
      maxHeight={isMobile ? '100%' : '90vh'}
      padding="0"
    >
      <ModalForm>
        <div
          ref={sheetRef}
          className="invoice-preview-modal-body"
        >
          <section className="invoice-preview-modal-section">
            <InvoiceGeneralInfo
              node={node}
              nodeType={nodeType}
              readOnly
              letterheadOverride={letterheadOverride}
            />
          </section>

          <section className="invoice-preview-modal-section">
            <InvoicePreviewItems
              node={node}
              nodeType={nodeType}
              neighborNodes={neighborNodes}
            />
          </section>

          <section className="invoice-preview-modal-section">
            <DocumentPayments
              node={node}
              nodeType={nodeType}
              readOnly
              previewMode
              neighborNodes={neighborNodes}
            />
          </section>

          {showTerms ? (
            <section className="invoice-preview-modal-section">
              <DocumentRichTextSection
                node={node}
                nodeType={nodeType}
                readOnly
                fieldName="termsAndConditions"
                title="Terms and Conditions"
              />
            </section>
          ) : null}

          {showMemo ? (
            <section className="invoice-preview-modal-section">
              <DocumentRichTextSection
                node={node}
                nodeType={nodeType}
                readOnly
                fieldName="memo"
                title="Memo"
              />
            </section>
          ) : null}
        </div>
      </ModalForm>

      <ModalButtons>
        <ModalButtonSection align="START">
          <Button
            label="Print"
            iconKey="print"
            onClick={handlePrint}
          />
        </ModalButtonSection>
        <ModalButtonSection align="END">
          <Button
            label="Close"
            onClick={onClose}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
});

// Dev Tools
InvoicePreviewModal.displayName = 'InvoicePreviewModal';

export default InvoicePreviewModal;
